import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { where } from 'firebase/firestore';
import type { BotDoc, SceneDoc, StoryRunDoc } from '@spirits/shared';
import { useGameStore } from '../store/useGameStore';
import { useDoc } from '../hooks/useDoc';
import { useCollection } from '../hooks/useCollection';
import { functions } from '../firebase';
import { EssenceChip } from '../lobby/EssenceChip';
import { PlayerNamePill } from '../lobby/PlayerNamePill';
import { SceneFeed, type FeedSelection } from '../scene/SceneFeed';
import { ScenePage } from '../scene/ScenePage';
import { GuidancePanel } from '../scene/GuidancePanel';
import { PlayerChat } from '../chat/PlayerChat';
import { withLocks } from '../scene/botOrder';
import { RecapPage } from '../play/RecapPage';
import { SceneTurnOverlay } from '../play/SceneTurnOverlay';
import { ShareRunButton } from '../play/ShareRunButton';
import { StoryInspector } from '../play/StoryInspector';
import { ScenePrompts } from '../play/ScenePrompts';

const joinStoryCallable = httpsCallable<{ storyId: string }, { ok: boolean; storyId: string }>(
  functions,
  'joinStory',
);

export default function PlayRoute() {
  const user = useGameStore((s) => s.user);
  const { storyId } = useParams<{ storyId: string }>();

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        <p className="text-xl tracking-wider">connecting to the unseen…</p>
      </div>
    );
  }
  if (!storyId) return <Navigate to="/" replace />;
  return <PlayAuthed uid={user.uid} storyId={storyId} />;
}

function PlayAuthed({ uid, storyId }: { uid: string; storyId: string }) {
  const { data: story, loading } = useDoc<StoryRunDoc>(`stories/${storyId}`);

  // Make the link an invite: auto-join the run if we're not yet a participant.
  const [joinAttempted, setJoinAttempted] = useState(false);
  useEffect(() => {
    if (!story || joinAttempted) return;
    if (!story.participants?.includes(uid)) {
      setJoinAttempted(true);
      joinStoryCallable({ storyId }).catch(() => {/* ignore — read-access still works */});
    }
  }, [story, uid, storyId, joinAttempted]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        <p className="text-xl tracking-wider">opening the story…</p>
      </div>
    );
  }
  if (!story) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 text-slate-400">
        <p className="text-lg">this run has drifted away.</p>
        <Link to="/" className="text-amber-200/90 hover:underline">← back to the lobby</Link>
      </div>
    );
  }

  return <SceneStage story={story} />;
}

function SceneStage({ story }: { story: StoryRunDoc & { id: string } }) {
  const currentSceneId = story.currentSceneId;
  // A selection is a "page": a scene (its setting) or a character.
  const [selected, setSelected] = useState<FeedSelection>(null);

  // The whole run: every scene + every scene's cast (past scenes stay browsable).
  // All scenes are minted up front from the authored spine; only `ready` ones (the
  // opening + those the generator has filled) are playable/visible.
  const { data: allScenes } = useCollection<SceneDoc>('scenes', where('storyId', '==', story.id));
  const scenes = useMemo(() => allScenes.filter((s) => s.ready), [allScenes]);
  const { data: bots } = useCollection<BotDoc>('bots', where('storyId', '==', story.id));

  const currentBots = useMemo(
    () => bots.filter((b) => b.sceneId === currentSceneId),
    [bots, currentSceneId],
  );
  const currentOrdered = useMemo(() => withLocks(currentBots), [currentBots]);

  const selectedBot = useMemo<(BotDoc & { id: string }) | null>(
    () => (selected?.kind === 'bot' ? bots.find((b) => b.id === selected.id) ?? null : null),
    [bots, selected],
  );
  const selectedSceneNode = useMemo(
    () => (selected?.kind === 'scene' ? scenes.find((s) => s.id === selected.id) ?? null : null),
    [scenes, selected],
  );
  const currentScene = useMemo(
    () => scenes.find((s) => s.id === currentSceneId) ?? null,
    [scenes, currentSceneId],
  );
  const botScene = useMemo(
    () => (selectedBot ? scenes.find((s) => s.id === selectedBot.sceneId) ?? null : null),
    [scenes, selectedBot],
  );
  const isCurrent = !!selectedBot && selectedBot.sceneId === currentSceneId;

  // The one bot the player can act on right now (current scene, lowest-ordered,
  // still deciding, unblocked). "Guide next" jumps here.
  const nextBot = useMemo(() => {
    const entry = currentOrdered.find((o) => o.bot.decisionState === 'deciding' && !o.locked);
    return entry ? entry.bot : null;
  }, [currentOrdered]);

  // The visible page order (matches the storyline feed) so a page can offer a
  // "Next →" without hopping to the left panel.
  const pages = useMemo<FeedSelection[]>(() => {
    const list: FeedSelection[] = [];
    [...scenes]
      .sort((a, b) => a.order - b.order)
      .forEach((s) => {
        list.push({ kind: 'scene', id: s.id });
        withLocks(bots.filter((b) => b.sceneId === s.id))
          .filter((o) => !o.locked)
          .forEach((o) => list.push({ kind: 'bot', id: o.bot.id }));
      });
    if (story.status === 'complete') list.push({ kind: 'ending', id: story.id });
    return list;
  }, [scenes, bots, story.status, story.id]);

  const nextPage = useMemo<FeedSelection>(() => {
    if (!selected) return null;
    const i = pages.findIndex((p) => p && p.kind === selected.kind && p.id === selected.id);
    return i >= 0 ? pages[i + 1] ?? null : null;
  }, [pages, selected]);

  const nextLabel = useMemo(() => {
    if (!nextPage) return null;
    if (nextPage.kind === 'ending') return 'The ending';
    if (nextPage.kind === 'bot') return bots.find((b) => b.id === nextPage.id)?.displayName ?? null;
    const s = scenes.find((x) => x.id === nextPage.id);
    return s ? `Scene ${s.order + 1}` : null;
  }, [nextPage, bots, scenes]);

  // Auto-select a PAGE on key transitions: the current scene as the run advances
  // (so the player reads the setting before guiding), or the ending recap once
  // the run completes. Only fires on the transition, so browsing stays free.
  const lastSceneRef = useRef<string | null>(null);
  useEffect(() => {
    if (story.status === 'complete') {
      if (lastSceneRef.current !== 'ending') {
        lastSceneRef.current = 'ending';
        setSelected({ kind: 'ending', id: story.id });
      }
      return;
    }
    if (!scenes.some((s) => s.id === currentSceneId)) return;
    if (lastSceneRef.current !== currentSceneId) {
      lastSceneRef.current = currentSceneId;
      setSelected({ kind: 'scene', id: currentSceneId });
    }
  }, [currentSceneId, scenes, story.status, story.id]);

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-slate-100 overflow-hidden">
      <header className="flex-none px-6 py-3 border-b border-slate-900 flex items-center justify-between gap-6">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/" className="flex items-center gap-1.5 text-slate-500 hover:text-slate-200 text-sm" title="lobby">
            <span className="text-lg leading-none">←</span>
            <span className="hidden sm:inline">lobby</span>
          </Link>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-amber-200/80 truncate">
              {story.title} · scene {story.sceneCount}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <StoryInspector story={story} bots={bots} />
          <ScenePrompts storyId={story.id} />
          <ShareRunButton story={story} />
          <EssenceChip />
          <PlayerNamePill playerName={useGameStore.getState().user?.playerName ?? ''} />
        </div>
      </header>

      <main className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-0">
        <section className={`${selected ? 'hidden lg:block' : 'block'} overflow-y-auto px-4 py-4 border-r border-slate-900`}>
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 px-1 pb-3">Storyline</p>
          <SceneFeed
            story={story}
            scenes={scenes}
            bots={bots}
            selected={selected}
            onSelect={setSelected}
          />
        </section>
        <section className={`min-h-0 items-stretch ${selected ? 'flex' : 'hidden lg:flex'} bg-slate-950`}>
          <div className="flex-1 min-h-0 min-w-0">
            {selected?.kind === 'ending' ? (
              <RecapPage story={story} onClose={() => setSelected(null)} />
            ) : selectedSceneNode ? (
              <ScenePage
                scene={selectedSceneNode}
                isCurrent={selectedSceneNode.id === currentSceneId}
                nextLabel={nextLabel}
                onNext={nextPage ? () => setSelected(nextPage) : undefined}
                onClose={() => setSelected(null)}
              />
            ) : (
              <GuidancePanel
                bot={selectedBot}
                scene={botScene ?? currentScene}
                soulCount={currentBots.length}
                isCurrent={isCurrent}
                nextBot={isCurrent && nextBot && nextBot.id !== selectedBot?.id ? nextBot : null}
                onSelectNext={(id) => setSelected({ kind: 'bot', id })}
                onClose={() => setSelected(null)}
              />
            )}
          </div>
          {/* The spirits' channel is story-wide — keep it docked beside every
              page (scene setting or character) instead of only the bot chat. */}
          <PlayerChat sceneId={currentSceneId} />
        </section>
      </main>

      {story.generating && <SceneTurnOverlay storyId={story.id} />}
    </div>
  );
}
